import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { createEnum } from "@/lib/utils/createEnum";
import { useTranslation } from "react-i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type C7Position = {
  id: number;
  date?: string;
  strategy_from_id?: number;
  strategy_from_name?: string;
  strategy_to_id?: number;
  strategy_to_name?: string;
  quantity?: number;
  weight?: number;
  market_value_local_ccy?: number;
  market_value_ptf_ccy?: number;
  time_serie_file_id?: number;
};

export const c7PositionFields = createEnum<C7Position>();

export const useC7PositionColumns = () => {
  const { t } = useTranslation([Tables.C7_POSITIONS, "tables"]);

  const columns: ColumnDef<C7Position>[] = [
    {
      accessorKey: c7PositionFields.id,
      header: "Id",
    },
    {
      accessorKey: c7PositionFields.date,
      header: () => t("columns.date", { ns: Tables.C7_POSITIONS }),
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue(c7PositionFields.date)))}
        </div>
      ),
    },
    {
      accessorKey: c7PositionFields.strategy_from_name,
      header: () =>
        t("columns.strategy_from_name", { ns: Tables.C7_POSITIONS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_from_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_from_name}
        </Link>
      ),
    },
    {
      accessorKey: c7PositionFields.strategy_to_name,
      header: () => t("columns.strategy_to_name", { ns: Tables.C7_POSITIONS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_to_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_to_name}
        </Link>
      ),
    },
    {
      accessorKey: c7PositionFields.quantity,
      header: () => (
        <div className="text-right">
          {t("columns.quantity", { ns: Tables.C7_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c7PositionFields.quantity))}
        </div>
      ),
    },
    {
      accessorKey: c7PositionFields.weight,
      header: () => (
        <div className="text-right">
          {t("columns.weight", { ns: Tables.C7_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c7PositionFields.weight))}
        </div>
      ),
    },
    {
      accessorKey: c7PositionFields.market_value_local_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.market_value_local_ccy", { ns: Tables.C7_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c7PositionFields.market_value_local_ccy))}
        </div>
      ),
    },
    {
      accessorKey: c7PositionFields.market_value_ptf_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.market_value_ptf_ccy", { ns: Tables.C7_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c7PositionFields.market_value_ptf_ccy))}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(c7PositionFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      ),
    },
  ];

  return columns;
};
